import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  GetVibeApprovalRequestPathVarsSchema,
  GetVibeApprovalRequestResponseSchema,
  GetVibeCapacityApprovalRequestPathVarsSchema,
  GetVibeCapacityApprovalRequestResponseSchema,
  ListPendingVibeApprovalsResponseSchema,
  ListPendingVibeCapacityApprovalsResponseSchema,
  RecordVibeApprovalDecisionBodySchema,
  RecordVibeApprovalDecisionPathVarsSchema,
  RecordVibeApprovalDecisionResponseSchema,
  RecordVibeCapacityApprovalDecisionBodySchema,
  RecordVibeCapacityApprovalDecisionPathVarsSchema,
  RecordVibeCapacityApprovalDecisionResponseSchema
} from "../schemas";

export const VibeApprovalEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeApprovalRequest — gates on AWAITING_APPROVAL deployments
  // ---------------------------------------------------------------------------

  /**
   * Fetch the approval request for a deployment, with the decisions
   * recorded so far. 404 if the deployment is not gated (no request
   * row) or not in the caller's org.
   */
  GetApprovalRequest: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/deployments/:deploymentId/approval",
    PathVars: GetVibeApprovalRequestPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeApprovalRequestResponseSchema)
  },

  /**
   * Record an APPROVE or REJECT decision. Self-approval (decider ==
   * deployment.createdByUserId) is denied at the use-case layer with
   * a 403. Duplicate decisions from the same user → 409. Decisions
   * against an already-terminal request → 409. Returns both the
   * persisted decision AND the request in its post-update state so
   * the caller sees the (possibly transitioned) request status
   * without a second round-trip.
   */
  RecordApprovalDecision: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/deployments/:deploymentId/approval/decisions",
    PathVars: RecordVibeApprovalDecisionPathVarsSchema,
    Body: RecordVibeApprovalDecisionBodySchema,
    Response: ApiSuccessSchema(RecordVibeApprovalDecisionResponseSchema)
  },

  /**
   * Reviewer dashboard data: every PENDING approval request across
   * the caller's org, ordered oldest-first (the FIFO most natural
   * for a reviewer triage queue). Newly expired rows are excluded
   * — the read uses `expiresAt > now()` so stale-but-not-yet-swept
   * rows don't surface.
   */
  ListPendingApprovals: {
    method: "GET" as const,
    path: "/api/vibe/approvals/pending",
    Response: ApiSuccessSchema(ListPendingVibeApprovalsResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeCapacityApprovalRequest — approval-gated elastic-storage grows
  // ---------------------------------------------------------------------------

  /**
   * The capacity reviewer queue: every PENDING grow request across the
   * caller's org, oldest-first, each carrying the disk pressure that raised
   * it. Newly expired rows are excluded (`expiresAt > now()`).
   *
   * Bounded to at most two rows: a partial unique index allows one PENDING
   * request per (organization, subject), and there are two subjects.
   *
   * ⚠️ Declared BEFORE `GetCapacityApprovalRequest` on the controller — the
   * sibling route is `/capacity-approvals/:requestId` with a uuid-parsed path
   * var, so a later declaration would match `pending` as the id and throw in
   * the Zod parse before the handler ran.
   */
  ListPendingCapacityApprovals: {
    method: "GET" as const,
    path: "/api/vibe/capacity-approvals/pending",
    Response: ApiSuccessSchema(ListPendingVibeCapacityApprovalsResponseSchema)
  },

  /**
   * Fetch a capacity approval request (a proposed node-size grow) with the
   * decisions recorded so far. 404 if the request is not in the caller's org.
   */
  GetCapacityApprovalRequest: {
    method: "GET" as const,
    path: "/api/vibe/capacity-approvals/:requestId",
    PathVars: GetVibeCapacityApprovalRequestPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeCapacityApprovalRequestResponseSchema)
  },

  /**
   * Record an APPROVE or REJECT decision on a capacity approval request.
   * Self-approval (decider == request.requestedByUserId) is denied at the
   * use-case layer with a 403. Duplicate decisions from the same user → 409;
   * decisions against an already-terminal or expired request → 409. On the
   * decisive APPROVE the approved target size is written onto the tenant
   * cluster and a converge is triggered — all in one transaction with the
   * decision + request flip + audit. Returns both the persisted decision and
   * the request in its post-update state.
   */
  RecordCapacityApprovalDecision: {
    method: "POST" as const,
    path: "/api/vibe/capacity-approvals/:requestId/decisions",
    PathVars: RecordVibeCapacityApprovalDecisionPathVarsSchema,
    Body: RecordVibeCapacityApprovalDecisionBodySchema,
    Response: ApiSuccessSchema(RecordVibeCapacityApprovalDecisionResponseSchema)
  }
} as const;
